import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useRef } from "react";

import { isProd } from "../../../utils/runtime-environment";
import Close from "../../icons/close-icon";
import styles from "./chat-dialog.module.scss";

export type ChatRegion = "AUNZ" | "UK" | "USCA";

export const localeChatRegionMap: Record<string, ChatRegion> = {
    "en-AU": "AUNZ",
    "en-NZ": "AUNZ",
    "en-GB": "UK",
    "es-ES": "UK",
    "fr-FR": "UK",
    "it-IT": "UK",
    "en-US": "USCA",
    "en-CA": "USCA",
    "fr-CA": "USCA",
};

// Deep links that hand off to the native app's support flow (mobile target).
const chatDeepLinkMap: Record<ChatRegion, string> = {
    USCA: "https://afterpayus.onelink.me/Ff2R/support",
    AUNZ: "https://afterpayau.onelink.me/nz1F/support",
    UK: "https://clearpay.onelink.me/ah1q/support",
};

export const getChatRegion = (locale: string): ChatRegion =>
    localeChatRegionMap[locale] ?? "USCA";

export const getChatDeepLink = (locale: string): string =>
    chatDeepLinkMap[getChatRegion(locale)];

const getChatQrCodeSrc = (locale: string): string => {
    const region = getChatRegion(locale);
    const env = isProd() ? "PROD" : "TEST";
    return `/images/chat/support_qr_code-${env}-${region}.png`;
};

interface ChatDialogProps {
    onClose: () => void;
}

const ChatDialog: React.FC<ChatDialogProps> = ({ onClose }) => {
    const { t } = useTranslation();
    const { locale } = useRouter();
    const qrCodeSrc = getChatQrCodeSrc(locale ?? "en-US");
    const modalRef = useRef<HTMLDivElement>(null);

    // Escape key handler
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [onClose]);

    // Auto-focus the close button when the dialog opens
    useEffect(() => {
        const closeBtn = modalRef.current?.querySelector<HTMLElement>("button");
        closeBtn?.focus();
    }, []);

    // Focus trap
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key !== "Tab" || !modalRef.current) return;

        const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length === 0) return;

        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
            if (document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
        } else {
            if (document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    }, []);

    return (
        <>
            <div className={styles.overlay} onClick={onClose} />
            <div className={styles.modalContainer}>
                <div
                    className={styles.modal}
                    ref={modalRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label={t("help.chat.title")}
                    onKeyDown={handleKeyDown}
                >
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label={t("help.close")}
                    >
                        <Close
                            color="#000000"
                            width={16}
                            height={16}
                            viewBoxWidth={24}
                            viewBoxHeight={24}
                        />
                    </button>
                    <div className={styles.content}>
                        <h5 className={styles.title}>{t("help.chat.title")}</h5>
                        <p className={styles.description}>{t("help.chat.description")}</p>
                        <div className={styles.qrCodeContainer}>
                            <img
                                src={qrCodeSrc}
                                alt={t("help.chat.qrCodeAlt")}
                                className={styles.qrCode}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ChatDialog;
